/* eslint-disable react/prop-types */
/* eslint-disable react/no-unknown-property */

import React, { useEffect, useRef, useState } from 'react';
import ContentLoader from 'react-content-loader';
import FoodImg from '../../common/images/food.png';
import { bleachedSilk, cerebralGrey, timeout } from '../../utils/constants';

const imageCache = new Map();

const preloadImage = (src, fallbackSrc, onDone) => {
	if (imageCache.has(src)) {
		onDone(imageCache.get(src));
		return () => {};
	}

	const img = new Image();
	let settled = false;

	const cleanup = () => {
		img.onload = null;
		img.onerror = null;
	};

	const finish = (result) => {
		if (settled) return;
		settled = true;
		clearTimeout(timer);
		cleanup();
		imageCache.set(src, result);
		onDone(result);
	};

	const timer = setTimeout(() => finish(fallbackSrc), timeout);

	img.onload = () => finish(src);
	img.onerror = () => finish(fallbackSrc);
	img.src = src;

	return () => {
		if (!settled) {
			cleanup();
			clearTimeout(timer);
			img.src = '';
		}
	};
};

const ImageLoader = (props) => {
	const { src = '', alt = '', className = 'h-full w-full rounded-xl object-cover object-center', priority = false } = props;
	const [imageSrc, setImageSrc] = useState(() => imageCache.get(src) || null);
	const [isNearViewport, setIsNearViewport] = useState(priority);
	const containerRef = useRef(null);

	useEffect(() => {
		if (priority) return;
		if (!containerRef.current || isNearViewport) return;

		if (typeof IntersectionObserver === 'undefined') {
			setIsNearViewport(true);
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					setIsNearViewport(true);
					observer.disconnect();
				}
			},
			{ rootMargin: '200px' },
		);

		observer.observe(containerRef.current);

		return () => observer.disconnect();
	}, [isNearViewport, priority]);

	useEffect(() => {
		if (!isNearViewport) return;

		if (!src) {
			setImageSrc(FoodImg);
			return;
		}

		if (imageCache.has(src)) {
			setImageSrc(imageCache.get(src));
			return;
		}

		setImageSrc(null);

		const cancelPreload = preloadImage(src, FoodImg, (result) => {
			setImageSrc(result);
		});

		return cancelPreload;
	}, [src, isNearViewport]);

	return (
		<div ref={containerRef} className={className}>
			{!imageSrc ? (
				<ContentLoader speed={2} width='100%' height='100%' backgroundColor={bleachedSilk} foregroundColor={cerebralGrey} className={className}>
					<rect x='0' y='0' rx='10' ry='10' width='100%' height='100%' />
				</ContentLoader>
			) : (
				<img alt={alt} src={imageSrc} className={className} loading={priority ? 'eager' : 'lazy'} decoding='async' fetchpriority={priority ? 'high' : 'auto'} />
			)}
		</div>
	);
};

export default React.memo(ImageLoader);
